// import PropTypes from 'prop-types';
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { noop } from 'lodash';
import writtenNumber from 'written-number';
import {
    LocaleService,
    WindowProxy,
    EmailService,
    ContentZonesService,
    CustomHooks,
} from '@sm360/widget-commons-core-services';
import {
    ModalService,
    AccountService,
    EVENTS,
    AnalyticsService,
    TRACKING_TAGS as TAGS,
} from '@sm360/widget-commons-general';
import { PurchaseMethods } from '@sm360/widget-commons-vehicle';
import { logger } from '@sm360/logger';
import 'trade-in-360/components/InstantEstimateWrapper/Steps/VehicleAppraisal/VehicleAppraisal.scss';
import TRADE_OPTIONS, { TRADE_OPTIONS_KEY } from '../../../../constants/tradeOptions.js';
import { tradeOnPurchaseImg, sellYourVehicleImg, notSureImg } from '../../../../constants/assetsUrl.js';
import AppraisalCard from './Components/AppraisalCard.jsx';
import OptionCard from './Components/OptionCard.jsx';
import NewTradeInContext from '../../../../NewTradeInContext.js';
import ActionButton from '../../Components/ActionButton/ActionButton.jsx';
import { getLocaleFormat } from '../../Utils/utils.js';

const { useScript } = CustomHooks;
const { PurchaseMethodsActions } = PurchaseMethods;

const getCZ = (name) => {
    return ContentZonesService.getContentZone('widget.tradein.teaser', name)
        ? ContentZonesService.getContentZone('widget.tradein.teaser', name).getValue()
        : undefined;
};

const trackSelfInspect = () => {
    AnalyticsService.gaTrigger(TAGS.TRADE_IN_SELF_INSPECT(WindowProxy.location().pathname));
};

const VehicleAppraisal = ({
    tradeInVehicle,
    onTradeInVehicleUpdated,
    onPreviousStep,
    closeModal,
    onMakeAppointment,
    account,
    handleEstimateChange,
    previousLabel,
    config,
}) => {
    const [selectedOption, setSelectedOption] = useState(tradeInVehicle?.selectedTradeOption || 0);
    const [fetching, setFetching] = useState(false);
    const { setIsLoginModalOpen, fieldsLocked, setFieldsLocked } = useContext(NewTradeInContext);
    const [currentLeadId, setCurrentLeadId] = useState(null);
    const [isCardUpdateLocked, setIsCardUpdateLocked] = useState(false);

    // Get content zone option texts
    const CZ_APPRAISAL_DESCRIPTION = getCZ('appraisal.description');
    const CZ_TRADE_ON_PURCHASE_DESCRIPTION = getCZ('appraisal.trade.on.purchase.description');
    const CZ_SELL_YOUR_VEHICLE_DESCRIPTION = getCZ('appraisal.sell.your.vehicle.description');
    const CZ_NOT_SURE_DESCRIPTION = getCZ('appraisal.not.sure.description');

    // Get content zone option images
    const CZ_TRADE_ON_PURCHASE_IMG = getCZ('appraisal.trade.on.purchase.image') || tradeOnPurchaseImg;
    const CZ_SELL_YOUR_VEHICLE_IMG = getCZ('appraisal.sell.your.vehicle.image') || sellYourVehicleImg;
    const CZ_NOT_SURE_IMG = getCZ('appraisal.not.sure.image') || notSureImg;

    /* Script requested by Dilawri for having Self-Inspect screen */
    useScript('https://dgoc-pave-app-5ukghzz6xq-uk.a.run.app/js/widget.js');

    // Scroll to options/appraisal amount when selected option changes
    // Wait for 300ms to let the open/collapse animation finish
    useEffect(
        () => {
            const currentOption = document.getElementById('appraisal-options');
            if (currentOption) setTimeout(() => currentOption.scrollIntoView({ behavior: 'smooth' }), 300);
        },
        [selectedOption]
    );

    // Lock all previous screens fields
    useEffect(() => {
        if (!fieldsLocked) {
            setFieldsLocked(true);
        }
    }, []);
    const SignUpModal = ModalService.get('login/SignUpModal');

    const dispatchTradeInCreatedEvent = () => {
        const $window = WindowProxy.get();
        if ($window) {
            $window.document.dispatchEvent(new Event(EVENTS.TRADEIN_CREATED));
        }
    };

    const saveTradeIn = ({ option, saveInfoOnly = false, existingAccount = false, asUniqueLead = false }) => {
        handleEstimateChange(tradeInVehicle.estimation.amount, option || selectedOption, existingAccount, asUniqueLead)
            .then(({ leadId }) => {
                // Call to add only basic info into already generated lead
                if (!saveInfoOnly) {
                    // Setup timeout to comply with lead creation on signup and on tradein
                    setTimeout(() => {
                        dispatchTradeInCreatedEvent();
                    }, 2000);
                    PurchaseMethodsActions.setTradeIn(tradeInVehicle.estimation);
                }
                setCurrentLeadId(leadId);
            })
            .finally(() => {
                setFetching(false);
                if (existingAccount) setIsCardUpdateLocked(true);
            });
    };

    const onLoginClose = useCallback(
        () => {
            setIsLoginModalOpen(false);
            // call to display loading screen since have timeout to save tradeIn
            if (AccountService.isAuthenticated()) {
                // Setup timeout to comply with lead creation on signup and on tradein
                setTimeout(() => {
                    saveTradeIn({ existingAccount: true });
                }, 1000);
            }
            return true;
        },
        [saveTradeIn]
    );

    const loginToSave = () => {
        SignUpModal.addCallbacks({
            onClose: onLoginClose,
        });
        SignUpModal.open({ context: 'widget.login.signup.information', account, hasConfirmation: false });
        setIsLoginModalOpen(true);
    };

    const handleKeepBrowsing = () => {
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_KEEP_BROWSING());
        closeModal();
    };

    const handleOptionSelection = (option) => {
        // Save selected option in upper parent's state (tradeInVehicle)
        onTradeInVehicleUpdated({ ...tradeInVehicle, selectedTradeOption: option });
        setSelectedOption(option);
        setFetching(true);

        const planTitleSelected = LocaleService.t(
            `TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.${TRADE_OPTIONS_KEY[option]}.TITLE`
        );
        AnalyticsService.ga4Trigger(TAGS.TRADE_IN_PLAN_SELECTION(planTitleSelected));

        if (AccountService.isAuthenticated()) {
            setTimeout(() => {
                saveTradeIn({ option, existingAccount: true, asUniqueLead: true });
            }, 3000);
        } else {
            /** Asynchronous lead creation force to add timeout between two tradeIn lead call
             * First tradeIn lead call when submit the estimation
             * Second call her by selecting the trade option
             * Removing the timeout would create an another tradeIn lead on second call in place update the first tradeIn lead
             * */
            setTimeout(() => {
                saveTradeIn({ option, saveInfoOnly: true, asUniqueLead: true });
            }, 5000);
        }
    };

    const handleSaveEstimate = () => {
        // If option is "Sell Your vehicle", send an email cheque
        if (selectedOption === TRADE_OPTIONS.SELL_VEHICLE) {
            if (currentLeadId) {
                const amount = tradeInVehicle?.estimation?.amount;
                const locale = LocaleService.getSimpleLocale();

                EmailService.sendTradeInChequeEmail({
                    websiteId: config?.websiteId,
                    locale,
                    leadId: currentLeadId,
                    clientName: `${tradeInVehicle?.contact?.firstname} ${tradeInVehicle?.contact?.lastname}`,
                    year: tradeInVehicle?.year,
                    make: tradeInVehicle?.make,
                    model: tradeInVehicle?.model,
                    trim: tradeInVehicle?.trim,
                    vin: tradeInVehicle?.vin,
                    exteriorColor: tradeInVehicle?.exteriorColor?.description,
                    amount: getLocaleFormat(amount, locale),
                    amountInLetters: typeof amount === 'number' ? writtenNumber(amount, { lang: locale }) : '',
                    conditionFormList: tradeInVehicle?.formattedConditionReport,
                    toEmail: tradeInVehicle?.contact?.email,
                    organizationUnitName: config?.orgUnitName || '',
                    organizationUnitPhone: config?.orgUnitPhone || '',
                    scheduleMonday: config?.scheduleMonday,
                    scheduleTuesday: config?.scheduleTuesday,
                    scheduleWednesday: config?.scheduleWednesday,
                    scheduleThursday: config?.scheduleThursday,
                    scheduleFriday: config?.scheduleFriday,
                    scheduleSaturday: config?.scheduleSaturday,
                    scheduleSunday: config?.scheduleSunday,
                    websiteDomain: config?.websiteDomain || '',
                    websiteLogo: config?.websiteLogo || '',
                    websiteFacebookUrl: config?.websiteFacebookUrl || '',
                    websiteInstagramUrl: config?.websiteInstagramUrl || '',
                    websiteTwitterUrl: config?.websiteTwitterUrl || '',
                    websiteYoutubeUrl: config?.websiteYoutubeUrl || '',
                }).catch((e) => {
                    logger.error(`Failed sending an email cheque for leadId=${currentLeadId}`, e);
                });
            } else {
                logger.error(
                    "Couldn't send Cheque Email for TradeIn: Sell Your Vehicle: leadId is not defined because lead hasn't been updated"
                );
            }
        }

        loginToSave();
    };

    return (
        <div className="appraisal-form">
            <form className="trade-in-form" onSubmit={noop}>
                <section className="appraisal-form--title">
                    <h3>{LocaleService.t('TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.TITLE')}</h3>
                    <p>
                        {CZ_APPRAISAL_DESCRIPTION ||
                            LocaleService.t('TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.DESCRIPTION')}
                    </p>
                </section>
                <section className="appraisal-form--options clearfix">
                    <p className="sr-text">
                        {LocaleService.t('TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.SELECT_ONE')}
                    </p>
                    <div className="appraisal-form-mobile-buttons vehicle-buttons-container">
                        <div className="vehicle-buttons">
                            <ActionButton
                                next
                                noPrefix
                                noArrow
                                noSubmit
                                selected={selectedOption === TRADE_OPTIONS.TRADE_PURCHASE}
                                onClick={() => handleOptionSelection(TRADE_OPTIONS.TRADE_PURCHASE)}
                                disabled={selectedOption > 0}
                            >
                                {LocaleService.t(
                                    'TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.TRADE_ON_PURCHASE.TITLE'
                                )}
                            </ActionButton>
                            <ActionButton
                                next
                                noPrefix
                                noArrow
                                noSubmit
                                selected={selectedOption === TRADE_OPTIONS.SELL_VEHICLE}
                                onClick={() => handleOptionSelection(TRADE_OPTIONS.SELL_VEHICLE)}
                                disabled={selectedOption > 0}
                            >
                                {LocaleService.t(
                                    'TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.SELL_VEHICLE.TITLE'
                                )}
                            </ActionButton>
                            <ActionButton
                                next
                                noPrefix
                                noArrow
                                noSubmit
                                selected={selectedOption === TRADE_OPTIONS.NOT_SURE}
                                onClick={() => handleOptionSelection(TRADE_OPTIONS.NOT_SURE)}
                                disabled={selectedOption > 0}
                            >
                                {LocaleService.t('TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.NOT_SURE.TITLE')}
                            </ActionButton>
                        </div>
                    </div>
                    <div className="appraisal-form-options-container" id="appraisal-options">
                        <OptionCard
                            img={CZ_TRADE_ON_PURCHASE_IMG}
                            title={LocaleService.t(
                                'TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.TRADE_ON_PURCHASE.TITLE'
                            )}
                            desc={
                                CZ_TRADE_ON_PURCHASE_DESCRIPTION ||
                                LocaleService.t(
                                    'TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.TRADE_ON_PURCHASE.DESCRIPTION'
                                )
                            }
                            onClick={() =>
                                // If authenticated, do tradeIn lead update with basic info
                                handleOptionSelection(TRADE_OPTIONS.TRADE_PURCHASE)
                            }
                            isSelected={selectedOption === TRADE_OPTIONS.TRADE_PURCHASE}
                            disabled={isCardUpdateLocked}
                        />
                        <OptionCard
                            img={CZ_SELL_YOUR_VEHICLE_IMG}
                            title={LocaleService.t(
                                'TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.SELL_VEHICLE.TITLE'
                            )}
                            desc={
                                CZ_SELL_YOUR_VEHICLE_DESCRIPTION ||
                                LocaleService.t(
                                    'TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.SELL_VEHICLE.DESCRIPTION'
                                )
                            }
                            onClick={() => handleOptionSelection(TRADE_OPTIONS.SELL_VEHICLE)}
                            isSelected={selectedOption === TRADE_OPTIONS.SELL_VEHICLE}
                            disabled={isCardUpdateLocked}
                        />
                        <OptionCard
                            img={CZ_NOT_SURE_IMG}
                            title={LocaleService.t(
                                'TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.NOT_SURE.TITLE'
                            )}
                            desc={
                                CZ_NOT_SURE_DESCRIPTION ||
                                LocaleService.t(
                                    'TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.NOT_SURE.DESCRIPTION'
                                )
                            }
                            onClick={() => handleOptionSelection(TRADE_OPTIONS.NOT_SURE)}
                            isSelected={selectedOption === TRADE_OPTIONS.NOT_SURE}
                            disabled={isCardUpdateLocked}
                        />
                    </div>
                </section>
                <AppraisalCard
                    value={tradeInVehicle?.estimation?.amount}
                    onMakeAppointment={onMakeAppointment}
                    onSaveEstimate={handleSaveEstimate}
                    onSelfInspect={trackSelfInspect}
                    isActive={selectedOption > 0}
                    fetching={fetching}
                    selectedOption={LocaleService.t(
                        `TRADE_IN.SECTIONS_CONTENT.VEHICLE_APPRAISAL.OPTIONS.${TRADE_OPTIONS_KEY[selectedOption]}.TITLE`
                    )}
                    tradeInVehicle={tradeInVehicle}
                />

                <section className="vehicle-buttons-container clearfix" data-test="trade-in.vehicle-form.submit">
                    <div className="vehicle-buttons">
                        <ActionButton onClick={() => onPreviousStep()} disabled={fetching}>
                            {previousLabel}
                        </ActionButton>
                        {closeModal && (
                            <ActionButton noPrefix noArrow onClick={handleKeepBrowsing} disabled={fetching}>
                                {LocaleService.t('TRADE_IN.BUTTONS.KEEP_BROWSING')}
                            </ActionButton>
                        )}
                    </div>
                </section>
            </form>
        </div>
    );
};

VehicleAppraisal.displayName = 'VehicleAppraisal';

export default VehicleAppraisal;
